import { useEffect, useId, useRef } from 'react';

export type ConfirmVariant = 'primary' | 'danger' | 'warning';

export interface ConfirmModalSecondaryAction {
  label: string;
  variant?: 'primary' | 'secondary' | 'danger' | 'warning' | 'outline-primary' | 'outline-secondary' | 'outline-danger';
  onClick: () => void;
}

interface ConfirmModalProps {
  show: boolean;
  title: string;
  body: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: ConfirmVariant;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  secondaryAction?: ConfirmModalSecondaryAction;
}

const VARIANT_CLASS: Record<ConfirmVariant, string> = {
  primary: 'btn-primary',
  danger: 'btn-danger',
  warning: 'btn-warning',
};

const SECONDARY_VARIANT_CLASS: Record<NonNullable<ConfirmModalSecondaryAction['variant']>, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  danger: 'btn-danger',
  warning: 'btn-warning',
  'outline-primary': 'btn-outline-primary',
  'outline-secondary': 'btn-outline-secondary',
  'outline-danger': 'btn-outline-danger',
};

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export default function ConfirmModal({
  show,
  title,
  body,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  confirmVariant = 'danger',
  loading = false,
  onConfirm,
  onCancel,
  secondaryAction,
}: ConfirmModalProps) {
  const titleId = useId();
  const bodyId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelBtnRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!show) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    document.body.classList.add('modal-open');
    document.body.setAttribute('data-modal-open', 'true');

    const focusTimer = window.setTimeout(() => {
      cancelBtnRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.body.classList.remove('modal-open');
      document.body.removeAttribute('data-modal-open');

      const previouslyFocused = previouslyFocusedRef.current;
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
  }, [show]);

  if (!show) return null;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCancel();
      return;
    }
    if (e.key === 'Tab' && dialogRef.current) {
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      ).filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <>
      <div className="modal-backdrop fade show" />
      <div
        ref={dialogRef}
        className="modal fade show d-block"
        tabIndex={-1}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onKeyDown={handleKeyDown}
        onMouseDown={handleBackdropClick}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id={titleId}>{title}</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Cerrar"
                onClick={onCancel}
                disabled={loading}
              />
            </div>
            <div className="modal-body" id={bodyId}>
              {body}
            </div>
            <div className="modal-footer">
              <button
                ref={cancelBtnRef}
                type="button"
                className="btn btn-secondary"
                onClick={onCancel}
                disabled={loading}
              >
                {cancelLabel}
              </button>
              {secondaryAction && (
                <button
                  type="button"
                  className={`btn ${SECONDARY_VARIANT_CLASS[secondaryAction.variant ?? 'outline-danger']}`}
                  onClick={secondaryAction.onClick}
                  disabled={loading}
                >
                  {secondaryAction.label}
                </button>
              )}
              <button
                type="button"
                className={`btn ${VARIANT_CLASS[confirmVariant]}`}
                onClick={onConfirm}
                disabled={loading}
              >
                {loading && (
                  <i
                    className="fa fa-spinner fa-spin me-1"
                    aria-hidden="true"
                  />
                )}
                {confirmLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}